/**
 * The Sales module's route table.
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads them in. Keeping them here means the paths, the
 * permissions and the components that implement them are decided in one place by
 * whoever owns the module.
 *
 * Three ordering and shape rules are load-bearing:
 *
 *  - `/…/new` is declared **before** `/…/:id(\d+)` everywhere, so `new` can never
 *    be parsed as a document id. The `\d+` constraint makes that safe anyway; the
 *    ordering makes it obvious.
 *  - The return "new" route carries its subject in the **query**
 *    (`?shipmentId=`) rather than the path. A return is best raised *against a
 *    shipment* — a query keeps the screen reachable with none chosen yet, which
 *    is what happens when somebody arrives from the nav rather than from a
 *    document. (`/sales/shipments/new` is a redirect to the picks since P45 U74.)
 *  - Subscription plans sit at their own path rather than under
 *    `/sales/subscriptions/plans`, because `plans` would otherwise have to be
 *    excluded from the `:id` pattern by hand.
 *
 * **Every route names one permission, and it is the permission the server guards
 * the screen's primary read with.** Not the write: a route gated on
 * `sales.order.create` would hide the order list from everyone who may only read
 * orders, and a route gated on nothing at all would render a screen whose every
 * request comes back 403. Write permissions are checked inside the screens, per
 * action, because §6 makes create / confirm / override a segregation-of-duties
 * set and no action may be inferred from another.
 *
 * **The four pricing routes are gone.** `/sales/price-lists`,
 * `/sales/price-lists/new`, `/sales/price-lists/:id` and `/sales/promotions`
 * moved to `PRICING_ROUTES` in `@/modules/pricing/routes` when C2 extracted the
 * engine into `internal/pricing`: the server routes they called are now
 * `/api/v1/pricing/...` under `pricing.price.read`, and leaving the old paths
 * mounted would leave four screens whose every request 404s. This packet's
 * report carries the redirects P9 should add so an existing bookmark lands on
 * the new screen rather than on the not-found page.
 *
 * Two routes are exceptions, and deliberately so:
 *
 *  - `/sales/settings` names `sales.settings.manage` because the server guards
 *    `GET /sales-settings` with the same code as the `PUT` — there is no
 *    read-only view of a sales policy to offer.
 *  - `/sales/returns/new` names its **create** permission, because unlike a
 *    document screen it has no read-only mode at all: it exists to post something.
 */

import type { RouteRecordRaw } from 'vue-router'

import { CREDIT_HOLD_ROUTES } from '@/modules/sales/credit-holds/routes'
import { DISCOUNT_ROUTES } from '@/modules/sales/discounts/routes'
import { FULFILMENT_ROUTES } from '@/modules/sales/fulfilment/routes'
import { SALES_PERMISSIONS } from '@/modules/sales/permissions'

export const SALES_ROUTES: RouteRecordRaw[] = [
  /* -------------------------------------------------------------- quotes */
  {
    path: '/sales/quotes',
    name: 'sales-quotes',
    component: () => import('@/modules/sales/QuotesListView.vue'),
    meta: { titleKey: 'sales.routeTitles.quotes', permission: SALES_PERMISSIONS.quoteRead },
  },
  {
    path: '/sales/quotes/new',
    name: 'sales-quote-new',
    component: () => import('@/modules/sales/QuoteView.vue'),
    meta: { titleKey: 'sales.routeTitles.quoteNew', permission: SALES_PERMISSIONS.quoteCreate },
  },
  {
    path: '/sales/quotes/:id(\\d+)',
    name: 'sales-quote',
    component: () => import('@/modules/sales/QuoteView.vue'),
    meta: { titleKey: 'sales.routeTitles.quote', permission: SALES_PERMISSIONS.quoteRead },
  },

  /* -------------------------------------------------------------- orders */
  {
    path: '/sales/orders',
    name: 'sales-orders',
    component: () => import('@/modules/sales/OrdersListView.vue'),
    meta: { titleKey: 'sales.routeTitles.orders', permission: SALES_PERMISSIONS.orderRead },
  },
  {
    path: '/sales/orders/new',
    name: 'sales-order-new',
    component: () => import('@/modules/sales/OrderView.vue'),
    meta: { titleKey: 'sales.routeTitles.orderNew', permission: SALES_PERMISSIONS.orderCreate },
  },
  {
    path: '/sales/orders/:id(\\d+)',
    name: 'sales-order',
    component: () => import('@/modules/sales/OrderView.vue'),
    meta: { titleKey: 'sales.routeTitles.order', permission: SALES_PERMISSIONS.orderRead },
  },
  /* P45 U67: the Credit Control queue (FSD 5.2.2). */
  ...CREDIT_HOLD_ROUTES,

  /* ----------------------------------------------------------- shipments */
  {
    path: '/sales/shipments',
    name: 'sales-shipments',
    component: () => import('@/modules/sales/ShipmentsListView.vue'),
    meta: { titleKey: 'sales.routeTitles.shipments', permission: SALES_PERMISSIONS.shipmentRead },
  },
  /*
   * P45 U74: the one-step "pick and ship" screen is gone (a stocked line ships only from a
   * completed pick, FSD 5.2.5 V08). An old bookmark lands on the picks, query (orderId) kept.
   */
  { path: '/sales/shipments/new', redirect: '/sales/picks' },
  {
    path: '/sales/shipments/:id(\\d+)',
    name: 'sales-shipment',
    component: () => import('@/modules/sales/ShipmentView.vue'),
    meta: { titleKey: 'sales.routeTitles.shipment', permission: SALES_PERMISSIONS.shipmentRead },
  },
  {
    path: '/sales/backorders',
    name: 'sales-backorders',
    component: () => import('@/modules/sales/BackordersListView.vue'),
    meta: { titleKey: 'sales.routeTitles.backorders', permission: SALES_PERMISSIONS.shipmentRead },
  },
  /* P45 U74: allocations, fulfilment orders and picking (FSD 5.2.5). */
  ...FULFILMENT_ROUTES,

  /* ------------------------------------------------------------- returns */
  {
    path: '/sales/returns',
    name: 'sales-returns',
    component: () => import('@/modules/sales/ReturnsListView.vue'),
    meta: { titleKey: 'sales.routeTitles.returns', permission: SALES_PERMISSIONS.returnRead },
  },
  {
    path: '/sales/returns/new',
    name: 'sales-return-new',
    component: () => import('@/modules/sales/ReturnView.vue'),
    meta: { titleKey: 'sales.routeTitles.returnNew', permission: SALES_PERMISSIONS.returnCreate },
  },
  {
    path: '/sales/returns/:id(\\d+)',
    name: 'sales-return',
    component: () => import('@/modules/sales/ReturnView.vue'),
    meta: { titleKey: 'sales.routeTitles.return', permission: SALES_PERMISSIONS.returnRead },
  },

  /* ------------------------------------------------------- subscriptions */
  {
    path: '/sales/subscriptions',
    name: 'sales-subscriptions',
    component: () => import('@/modules/sales/SubscriptionsListView.vue'),
    meta: {
      titleKey: 'sales.routeTitles.subscriptions',
      permission: SALES_PERMISSIONS.subscriptionRead,
    },
  },
  {
    path: '/sales/subscriptions/:id(\\d+)',
    name: 'sales-subscription',
    component: () => import('@/modules/sales/SubscriptionView.vue'),
    meta: {
      titleKey: 'sales.routeTitles.subscription',
      permission: SALES_PERMISSIONS.subscriptionRead,
    },
  },
  {
    path: '/sales/subscription-plans',
    name: 'sales-subscription-plans',
    component: () => import('@/modules/sales/SubscriptionPlansView.vue'),
    meta: {
      titleKey: 'sales.routeTitles.subscriptionPlans',
      permission: SALES_PERMISSIONS.subscriptionRead,
    },
  },

  /* -------------------------------------------------- revenue recognition */
  {
    path: '/sales/revrec',
    name: 'sales-revrec',
    component: () => import('@/modules/sales/RevRecListView.vue'),
    meta: { titleKey: 'sales.routeTitles.revRec', permission: SALES_PERMISSIONS.revRecRead },
  },
  {
    path: '/sales/revrec/:id(\\d+)',
    name: 'sales-revrec-schedule',
    component: () => import('@/modules/sales/RevRecScheduleView.vue'),
    meta: {
      titleKey: 'sales.routeTitles.revRecSchedule',
      permission: SALES_PERMISSIONS.revRecRead,
    },
  },

  /* --------------------------------------- C2 commercial terms (design §11) */

  /*
   * Six master-detail screens, each named for the **read** permission the server
   * guards its list with — the same rule as every route above. The two-code
   * split (`…read` / `…manage`) is the server's, and the manage half is checked
   * per action inside the screen: a Sales Operations analyst who may read the
   * authority matrix but not change it gets the matrix, not a 403.
   *
   * `sales.rebate.settle` guards no route at all, deliberately. Settlement is an
   * action on an accrual, not a place — and it is in declared SoD conflict with
   * `sales.rebate.manage`, so a route that required both would be a screen
   * nobody may legitimately hold.
   */
  {
    path: '/sales/trade-agreements',
    name: 'sales-trade-agreements',
    component: () => import('@/modules/sales/TradeAgreementsView.vue'),
    meta: {
      titleKey: 'sales.routeTitles.tradeAgreements',
      permission: SALES_PERMISSIONS.agreementRead,
    },
  },
  {
    path: '/sales/rebate-programs',
    name: 'sales-rebate-programs',
    component: () => import('@/modules/sales/RebateProgramsView.vue'),
    meta: {
      titleKey: 'sales.routeTitles.rebatePrograms',
      permission: SALES_PERMISSIONS.rebateRead,
    },
  },
  {
    path: '/sales/rebate-accruals',
    name: 'sales-rebate-accruals',
    component: () => import('@/modules/sales/RebateAccrualsView.vue'),
    meta: {
      titleKey: 'sales.routeTitles.rebateAccruals',
      permission: SALES_PERMISSIONS.rebateRead,
    },
  },
  {
    path: '/sales/customer-contracts',
    name: 'sales-customer-contracts',
    component: () => import('@/modules/sales/CustomerContractsView.vue'),
    meta: {
      titleKey: 'sales.routeTitles.customerContracts',
      permission: SALES_PERMISSIONS.contractRead,
    },
  },
  /* P45 UDM: the authority matrix, discount types and margin floors (FSD 5.2.4). */
  ...DISCOUNT_ROUTES,
  {
    path: '/sales/obligations',
    name: 'sales-obligations',
    component: () => import('@/modules/sales/ObligationsView.vue'),
    meta: {
      titleKey: 'sales.routeTitles.obligations',
      permission: SALES_PERMISSIONS.obligationRead,
    },
  },

  /* ------------------------------------------------------------- reports */
  {
    path: '/sales/reports/pipeline',
    name: 'sales-pipeline',
    component: () => import('@/modules/sales/PipelineReportView.vue'),
    meta: { titleKey: 'sales.routeTitles.pipeline', permission: SALES_PERMISSIONS.reportRead },
  },
  {
    path: '/sales/reports/sales-by',
    name: 'sales-by',
    component: () => import('@/modules/sales/SalesByReportView.vue'),
    meta: { titleKey: 'sales.routeTitles.salesBy', permission: SALES_PERMISSIONS.reportRead },
  },
  {
    path: '/sales/reports/mrr',
    name: 'sales-mrr',
    component: () => import('@/modules/sales/MrrReportView.vue'),
    meta: { titleKey: 'sales.routeTitles.mrr', permission: SALES_PERMISSIONS.reportRead },
  },

  /* ------------------------------------------------------------ settings */
  {
    path: '/sales/settings',
    name: 'sales-settings',
    component: () => import('@/modules/sales/SettingsView.vue'),
    meta: {
      titleKey: 'sales.routeTitles.settings',
      permission: SALES_PERMISSIONS.settingsManage,
    },
  },
]
